"use client";
import { Token } from "@uniswap/sdk-core";
import QuoterV2 from "@uniswap/v3-periphery/artifacts/contracts/lens/QuoterV2.sol/QuoterV2.json";
import { FeeAmount } from "@uniswap/v3-sdk";
import { ConnectKitButton } from "connectkit";
import debounce from "lodash.debounce";
import { ArrowUpDown, Loader2, RefreshCw } from "lucide-react";
import Image from "next/image";
import type React from "react";
import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { twMerge } from "tailwind-merge";
import { erc20Abi, formatUnits, parseUnits } from "viem";
import { readContract, simulateContract } from "viem/actions";
import { useAccount, usePublicClient, useReadContract, useWaitForTransactionReceipt, useWriteContract } from "wagmi";
import Button from "../Button";
import { CHAIN_ID } from "../kolToken/constants";
import { Button as StandardButton } from "../ui/button";
import ApprovalSuccessModal from "./ApprovalSuccessModal";
import SwapSuccessModal from "./SwapSuccessModal";
import {
  DEBOUNCE_TIME,
  KOLZ_TOKEN_ADDRESS,
  KOLZ_TOKEN_COINGECKO_ID,
  KOLZ_TOKEN_DECIMALS,
  KOLZ_TOKEN_SYMBOL,
  QUOTER_ADDRESS,
  SWAP_ROUTER_ABI,
  SWAP_ROUTER_ADDRESS,
} from "./constants";

interface SwapToken {
  token: Token;
  coingeckoSymbol: string;
}

// Reference token (USDC) used as the default swap target
const REFERENCE_TOKEN = {
  token: new Token(CHAIN_ID, KOLZ_TOKEN_ADDRESS, Number(KOLZ_TOKEN_DECIMALS), KOLZ_TOKEN_SYMBOL),
  coingeckoSymbol: KOLZ_TOKEN_COINGECKO_ID,
} as SwapToken;

const DEFAULT_AMOUNT = 10;

/**
 * Data transport object for token info
 */

export interface TokenInfo {
  symbol: string; // Token symbol (e.g., "KOLZ")
  erc20Address: string; // Token contract address
  decimals: number; // Token decimal places
  coingeckoSymbol: string; // CoinGecko API identifier for the token
}

/**
 * Fetches JSON data from a URL
 */
const fetcher = (url: string) => fetch(url).then((res) => res.json());

const formatUsdValue = (amount: number, unitPrice: number | undefined): string => {
  if (!unitPrice || amount === 0) return "";
  const usdValue = amount * unitPrice;
  return usdValue < 0.01 ? "< $0.01" : `≈ $${usdValue.toFixed(2)}`;
};

function amountBigintToNumber(amount: bigint, decimals: number): number {
  return Number(formatUnits(amount, decimals));
}

function amountBigintToString(amount: bigint, decimals: number): string {
  return Number(formatUnits(amount, decimals)).toFixed(5);
}

function amountRate(amount1: number, amount2: number): number {
  return amount2 / amount1;
}

/**
 * SwapWidget component for token swapping via Uniswap V3
 */
export default function SwapWidget({ tokenInfo }: { tokenInfo: TokenInfo }) {
  // CoinGecko API URL for fetching token prices
  const FEE = FeeAmount.MEDIUM;
  const tokenInfoCoingecko = tokenInfo.coingeckoSymbol ? `${tokenInfo.coingeckoSymbol},` : "";
  const PRICE_API = `https://api.coingecko.com/api/v3/simple/price?vs_currencies=usd&ids=${tokenInfoCoingecko}${REFERENCE_TOKEN.coingeckoSymbol}`;

  const { data: priceData } = useSWR(PRICE_API, fetcher, {
    refreshInterval: 60000, // refresh every minute
    revalidateOnFocus: false,
  });

  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [showApprovalModal, setShowApprovalModal] = useState<boolean>(false);
  const [swapDetails, setSwapDetails] = useState<{
    inputAmount: string;
    outputAmount: string;
    inputTokenSymbol: string;
    outputTokenSymbol: string;
  }>({
    inputAmount: "",
    outputAmount: "",
    inputTokenSymbol: "",
    outputTokenSymbol: "",
  });

  // State for the tokens being swapped
  const [token0, setToken0] = useState<SwapToken>({
    token: new Token(CHAIN_ID, tokenInfo.erc20Address, tokenInfo.decimals, tokenInfo.symbol, tokenInfo.symbol),
    coingeckoSymbol: tokenInfo.coingeckoSymbol,
  });
  const [token1, setToken1] = useState<SwapToken>(REFERENCE_TOKEN);

  // State for swap UI and transaction status
  const [isApproved, setIsApproved] = useState<boolean>(false);
  const [isQuoteLoading, setIsQuoteLoading] = useState<boolean>(false);
  const [tokenOut, setTokenOut] = useState<bigint>(); // Expected output amount (raw)
  // const [outputAmount, setOutputAmount] = useState<string>(); // Formatted output amount
  const outputAmount = tokenOut ? amountBigintToNumber(tokenOut, token1.token.decimals) : undefined;
  const [inputAmount, setInputAmount] = useState<number>(DEFAULT_AMOUNT);
  const tokenIn = parseUnits(inputAmount.toString(), token0.token.decimals); // Input amount in raw units

  const unitPriceIn =
    priceData?.[token0.coingeckoSymbol]?.usd ??
    (outputAmount ? priceData?.[token1.coingeckoSymbol]?.usd * (outputAmount / inputAmount) : undefined);

  const unitPriceOut =
    priceData?.[token1.coingeckoSymbol]?.usd ??
    (outputAmount ? priceData?.[token0.coingeckoSymbol]?.usd * (inputAmount / outputAmount) : undefined);

  const renderOutputAmount = tokenOut ? amountBigintToString(tokenOut, token1.token.decimals) : undefined;
  const renderInputAmount = amountBigintToString(tokenIn, token0.token.decimals);

  const [error, setError] = useState<string | null>(null);
  const rate = outputAmount && !isQuoteLoading ? amountRate(inputAmount, outputAmount) : undefined;

  // Wagmi hooks for blockchain interaction
  const client = usePublicClient();
  const { address } = useAccount();
  const isConnected = !!address;

  // Read token balances
  const { data: token0BalanceData, refetch: refetchBalance0 } = useReadContract({
    address: token0.token.address as `0x${string}`,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: [address as `0x${string}`],
    query: {
      enabled: !!address,
    },
  });

  const { data: token1BalanceData, refetch: refetchBalance1 } = useReadContract({
    address: token1.token.address as `0x${string}`,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: [address as `0x${string}`],
    query: {
      enabled: !!address,
    },
  });

  // Create contract interface for the ERC20 token being swapped
  const erc20Contract = useMemo(
    () =>
      ({
        address: token0.token.address as `0x${string}`,
        abi: erc20Abi,
      }) as const,
    [token0],
  );

  // Hooks for writing to blockchain
  const { writeContract: executeSwap, data: txHashSwap, reset: resetExecuteSwap } = useWriteContract();
  const { writeContract: grantAllowance, data: txHashAllowance, reset: resetGrantAllowance } = useWriteContract();

  // Transaction status hooks
  const { isPending: isPendingSwap, isSuccess: isSuccessSwap } = useWaitForTransactionReceipt({
    confirmations: 2,
    hash: txHashSwap,
  });

  const { isPending: isPendingAllowance, isSuccess: isSuccessAllowance } = useWaitForTransactionReceipt({
    confirmations: 2,
    hash: txHashAllowance,
  });

  // Helper functions for component state
  const waitingForAllowance = txHashAllowance && isPendingAllowance;
  const waitingForSwap = txHashSwap && isPendingSwap;
  const isInputDisabled = waitingForSwap || waitingForAllowance;
  const isSwapButtonDisabled = !tokenOut || !isApproved || waitingForSwap || tokenIn <= BigInt(0);
  const isApproveButtonDisabled = waitingForAllowance || tokenIn <= BigInt(0) || !tokenOut;
  const isSwitchButtonDisabled = isQuoteLoading || waitingForAllowance || waitingForSwap;
  const hasValidInput = tokenIn > BigInt(0);
  const hasValidOutput = tokenOut && Number(tokenOut) > 0;

  const showUsdValue = (amount: string | undefined, unitPrice: number | undefined, isQuoteLoading: boolean) =>
    amount && unitPrice && unitPrice > 0 && !isQuoteLoading;

  const token0Balance = token0BalanceData ? formatUnits(token0BalanceData as bigint, token0.token.decimals) : "0";
  const token1Balance = token1BalanceData ? formatUnits(token1BalanceData as bigint, token1.token.decimals) : "0";

  // SECTION: Smart contract interactions for token swapping

  function approve() {
    if (!tokenIn || tokenIn <= BigInt(0)) return;

    grantAllowance({
      ...erc20Contract,
      functionName: "approve",
      args: [SWAP_ROUTER_ADDRESS, BigInt(tokenIn)],
    });
  }

  async function swap() {
    if (!tokenOut || !isApproved) return;

    executeSwap({
      address: SWAP_ROUTER_ADDRESS,
      abi: SWAP_ROUTER_ABI,
      functionName: "exactInputSingle",
      args: [
        {
          tokenIn: token0.token.address,
          tokenOut: token1.token.address,
          fee: FEE,
          recipient: address,
          amountIn: tokenIn,
          amountOutMinimum: tokenOut, // Minimum amount to receive (no slippage protection implemented)
          sqrtPriceLimitX96: 0, // No price limit
        },
      ],
    });
  }

  /**
   * Checks if the user has approved enough tokens for the swap
   * @returns Boolean indicating if the allowance is sufficient
   */
  const checkAllowance = useCallback(async () => {
    if (client && address && tokenIn) {
      try {
        const allowance = await readContract(client, {
          ...erc20Contract,
          functionName: "allowance",
          args: [address, SWAP_ROUTER_ADDRESS],
        });

        setIsApproved(allowance >= tokenIn);
        return allowance >= tokenIn;
      } catch (e) {
        console.error("Error checking allowance:", e);
        setIsApproved(false);
        return false;
      }
    }
    return false;
  }, [client, address, tokenIn, erc20Contract]);

  // SECTION: Quote fetching and input handling

  /**
   * Fetches a quote for the swap from Uniswap's Quoter contract
   */
  const fetchQuote = useCallback(async () => {
    if (client !== undefined && tokenIn > BigInt(0)) {
      setError(null);
      try {
        // Use Uniswap's QuoterV2 contract to get the expected output amount
        const { result } = await simulateContract(client, {
          abi: QuoterV2.abi,
          address: QUOTER_ADDRESS,
          functionName: "quoteExactInputSingle",
          args: [
            {
              tokenIn: token0.token.address,
              tokenOut: token1.token.address,
              amountIn: tokenIn,
              fee: FEE,
              sqrtPriceLimitX96: 0, // No price limit
            },
          ],
        });
        setTokenOut(result[0]);
        await checkAllowance(); // Check if user has approved enough tokens
      } catch (e) {
        console.error("Error fetching quote:", e);
        setError("Failed to get quote. Please try again.");
        setTokenOut(undefined);
      } finally {
        setIsQuoteLoading(false);
      }
    }
  }, [tokenIn, FEE, client, token1, token0, checkAllowance]);

  // Create a debounced version of fetchQuote to avoid excessive API calls
  const debouncedFetchQuote = useMemo(() => debounce(fetchQuote, DEBOUNCE_TIME), [fetchQuote]);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and a single decimal point
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setInputAmount(Number(value));
    }
  }, []);

  const refreshQuote = () => {
    if (tokenIn > BigInt(0)) {
      setIsQuoteLoading(true);
      fetchQuote();
    }
  };

  const handleSwitchTokens = useCallback(() => {
    // Switch token objects
    setToken0(token1);
    setToken1(token0);

    // Reset state
    setTokenOut(undefined);
    setInputAmount(outputAmount || DEFAULT_AMOUNT);
    setError(null);
  }, [token0, token1, outputAmount]);

  // SECTION: Effect hooks for component lifecycle and state management

  // Reset State
  useEffect(() => {
    setIsQuoteLoading(false);

    setInputAmount(DEFAULT_AMOUNT);

    setToken1({
      token: new Token(CHAIN_ID, tokenInfo.erc20Address, tokenInfo.decimals, tokenInfo.symbol, tokenInfo.symbol),
      coingeckoSymbol: tokenInfo.coingeckoSymbol,
    });
    setToken0(REFERENCE_TOKEN);
    setTokenOut(undefined);
    setIsApproved(false);
    setError(null);
  }, [tokenInfo]);

  // Fetch a new quote when input amount or fee changes
  useEffect(() => {
    if (tokenIn > BigInt(0)) {
      setIsQuoteLoading(true);
      debouncedFetchQuote();
    } else {
      setTokenOut(undefined);
    }

    // Clean up debounce on unmount
    return () => {
      debouncedFetchQuote.cancel();
    };
  }, [tokenIn, debouncedFetchQuote]);

  // Update approval status when allowance transaction succeeds
  useEffect(() => {
    if (isSuccessAllowance) {
      setIsApproved(true);
      resetGrantAllowance();
      setShowApprovalModal(true);
    }
  }, [isSuccessAllowance, resetGrantAllowance]);

  // Reset form after successful swap
  useEffect(() => {
    if (isSuccessSwap) {
      // Save swap details before resetting
      setSwapDetails({
        inputAmount: renderInputAmount || "0",
        outputAmount: renderOutputAmount || "0",
        inputTokenSymbol: token0.token.symbol as string,
        outputTokenSymbol: token1.token.symbol as string,
      });

      // Reset input after successful swap
      setInputAmount(10);
      resetExecuteSwap();

      // Show success modal instead of toast
      setShowSuccessModal(true);

      refetchBalance0();
      refetchBalance1();
    }
  }, [
    isSuccessSwap,
    resetExecuteSwap,
    renderInputAmount,
    renderOutputAmount,
    token0.token.symbol,
    token1.token.symbol,
    refetchBalance0,
    refetchBalance1,
  ]);

  // SECTION: Component UI rendering
  return (
    <div className="bg-gray-800/50 rounded-xl shadow-lg p-4 md:p-6 w-full mx-auto border border-gray-700/50 h-full">
      <SwapSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        inputAmount={swapDetails.inputAmount}
        outputAmount={swapDetails.outputAmount}
        inputTokenSymbol={swapDetails.inputTokenSymbol}
        outputTokenSymbol={swapDetails.outputTokenSymbol}
      />
      <ApprovalSuccessModal
        isOpen={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        tokenSymbol={token0.token.symbol as string}
        amount={inputAmount.toString()}
      />
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-white">Swap Tokens</h2>
        <Button
          onClick={refreshQuote}
          className="text-blue-400 hover:text-blue-300 transition-colors"
          disabled={isQuoteLoading}
        >
          <RefreshCw size={18} className={isQuoteLoading ? "animate-spin" : ""} />
        </Button>
      </div>

      {/* Input Token */}
      <div className="bg-gray-800/80 rounded-lg p-4 mb-2 border border-gray-700/50">
        <div className="flex justify-between mb-2">
          <span className="text-gray-400 text-sm">From</span>
          <div className="flex items-center">
            <Image
              width={100}
              height={100}
              src={`/token_logos/${(token0.token.symbol as string).toLowerCase()}.png`}
              alt={token0.token.symbol as string}
              className="w-5 h-5 mr-1"
              onError={(e) => {
                (e.target as HTMLImageElement).src = "/token_logos/usdc.png";
              }}
            />
            <span className="text-white font-medium">{token0.token.symbol}</span>
          </div>
        </div>
        {isConnected && (
          <div className="flex justify-end mb-1">
            <span className="text-gray-400 text-xs">
              Balance: {Number.parseFloat(token0Balance).toFixed(6)} {token0.token.symbol}
            </span>
          </div>
        )}
        <div className="flex flex-col">
          <div className="flex items-center">
            <input
              type="text"
              value={inputAmount}
              onChange={handleInputChange}
              placeholder="0.0"
              className="bg-gray-700/80 text-white text-2xl w-full outline-none px-3 py-1 rounded-md focus:ring-2 focus:ring-blue-500"
              disabled={isInputDisabled}
            />
          </div>
          <div className="text-xs text-gray-500 mt-1 h-4">
            {showUsdValue(renderInputAmount, unitPriceIn, isQuoteLoading) && formatUsdValue(inputAmount, unitPriceIn)}
          </div>
        </div>
      </div>

      {/* Arrow with Switch Button */}
      <div className="flex justify-center my-2 relative">
        <StandardButton
          onClick={handleSwitchTokens}
          className="bg-gray-700 hover:bg-gray-600 p-2 rounded-full transition-colors"
          disabled={isSwitchButtonDisabled}
        >
          <ArrowUpDown size={16} className="text-gray-300" />
        </StandardButton>
      </div>

      {/* Output Token */}
      <div className="bg-gray-800/80 rounded-lg p-4 mb-4 border border-gray-700/50">
        <div className="flex justify-between mb-2">
          <span className="text-gray-400 text-sm">To</span>
          <div className="flex items-center">
            <Image
              width={100}
              height={100}
              src={`/token_logos/${token1.token.symbol?.toLowerCase()}.png`}
              alt={(token1.token.symbol as string).toLowerCase()}
              className="w-5 h-5 mr-1"
            />
            <span className="text-white font-medium">{token1.token.symbol}</span>
          </div>
        </div>
        {isConnected && (
          <div className="flex justify-end mb-1">
            <span className="text-gray-400 text-xs">
              Balance: {Number.parseFloat(token1Balance).toFixed(6)} {token1.token.symbol}
            </span>
          </div>
        )}
        <div className="flex flex-col">
          <div className="flex items-center">
            <div className="bg-gray-700/80 text-white text-2xl w-full px-3 py-1 rounded-md">
              {isQuoteLoading ? (
                <div className="flex items-center">
                  <Loader2 size={20} className="animate-spin mr-2 text-gray-400" />
                  <span className="text-gray-400">Fetching quote...</span>
                </div>
              ) : tokenOut ? (
                renderOutputAmount
              ) : (
                <span className="text-gray-500">0.0</span>
              )}
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-1 h-4">
            {outputAmount &&
              showUsdValue(renderOutputAmount, unitPriceOut, isQuoteLoading) &&
              formatUsdValue(outputAmount, unitPriceOut)}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && <div className="bg-red-900/30 text-red-400 p-3 rounded-lg mb-4 text-sm">{error}</div>}

      {/* Action Button */}
      {!isConnected ? (
        <ConnectKitButton.Custom>
          {({ show }) => (
            <Button onClick={show} type="button" className="w-full flex justify-center items-center ml-2">
              Connect Wallet to Swap
            </Button>
          )}
        </ConnectKitButton.Custom>
      ) : !isApproved && hasValidInput ? (
        <StandardButton
          onClick={approve}
          disabled={isApproveButtonDisabled}
          className={twMerge(
            "w-full py-3 rounded-lg font-medium transition-all",
            isApproveButtonDisabled
              ? "bg-blue-900/50 text-blue-300/50 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700 text-white",
          )}
        >
          {waitingForAllowance ? (
            <div className="flex items-center justify-center">
              <Loader2 size={20} className="animate-spin mr-2" />
              <span>Approving...</span>
            </div>
          ) : (
            `Approve ${token0.token.symbol}`
          )}
        </StandardButton>
      ) : (
        <StandardButton
          onClick={swap}
          disabled={isSwapButtonDisabled}
          className={twMerge(
            "w-full py-3 rounded-lg font-medium transition-all",
            isSwapButtonDisabled
              ? "bg-blue-900/50 text-blue-300/50 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700 text-white",
          )}
        >
          {waitingForSwap ? (
            <div className="flex items-center justify-center">
              <Loader2 size={20} className="animate-spin mr-2" />
              <span>Swapping...</span>
            </div>
          ) : !tokenOut ? (
            "Enter an amount"
          ) : (
            "Swap"
          )}
        </StandardButton>
      )}

      {/* Rate */}
      {hasValidInput && rate && (
        <div className="mt-4 text-gray-400 text-sm text-center">
          {hasValidOutput ? (
            <>
              <div>
                Rate: 1 {token0.token.symbol} ≈ {rate ? rate.toFixed(16).replace(/\.?0+$/, "") : "..."}{" "}
                {token1.token.symbol}
              </div>
              {unitPriceOut && rate && (
                <div className="text-xs text-gray-500 mt-1">
                  {rate * unitPriceOut < 0.01 ? "< $0.01" : `≈ $${(rate * unitPriceOut).toFixed(2)}`}
                </div>
              )}
            </>
          ) : renderOutputAmount ? (
            <div className="text-red-400">No liquidity available for this amount.</div>
          ) : (
            ""
          )}
        </div>
      )}
    </div>
  );
}
